import React, { useState, FC } from "react";
import { X, Save, RefreshCw } from "lucide-react";
import { Clip } from "../../types";

interface EditLibraryItemModalProps {
    item: any;
    selectedClip: Clip | null;
    onClose: () => void;
    onUpdate: (itemId: string, changes: any) => Promise<void>;
    onMasterSaveAndExit?: () => Promise<void>;
}

export const EditLibraryItemModal: FC<EditLibraryItemModalProps> = ({ item, selectedClip, onClose, onUpdate, onMasterSaveAndExit }) => {
    const [name, setName] = useState(item.name);
    const [shouldUpdateContent, setShouldUpdateContent] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Check if we can update content from timeline
    const canUpdateContent = (item.category === 'synthes' || item.category === 'panels') &&
        selectedClip &&
        selectedClip.type === (item.type || (item.category === 'synthes' ? 'synthe' : 'panel'));

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const changes: any = {};
            if (name.trim() !== item.name) {
                changes.name = name.trim();
            }

            if (shouldUpdateContent && selectedClip) {
                changes.syntheData = selectedClip.syntheData;
                changes.duration = selectedClip.duration;
            }

            if (Object.keys(changes).length > 0) {
                await onUpdate(item.id, changes);
            }

            // If we have master save+exit callback, save master content and exit
            if (onMasterSaveAndExit) {
                await onMasterSaveAndExit();
            } else {
                onClose();
            }
        } catch (error) {
            console.error("Update failed:", error);
            alert("Erreur lors de la mise à jour.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-md flex flex-col border border-gray-700 text-white" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-gray-700 flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Modifier le modèle</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-700 text-gray-300">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    <div>
                        <label className="text-sm font-medium text-gray-300 block mb-1.5">Nom</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full text-sm p-2.5 border border-gray-600 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={isSaving}
                        />
                    </div>

                    {canUpdateContent && (
                        <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
                            <div className="flex items-start gap-3">
                                <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                                    <RefreshCw className="w-5 h-5" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="font-medium text-sm text-gray-200">Mettre à jour le contenu</h4>
                                    <p className="text-xs text-gray-400 mt-1">
                                        Remplacer le contenu du modèle par les réglages de l'élément actuellement sélectionné dans la timeline.
                                    </p>
                                    <label className="flex items-center gap-2 mt-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={shouldUpdateContent}
                                            onChange={(e) => setShouldUpdateContent(e.target.checked)}
                                            className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                                            disabled={isSaving}
                                        />
                                        <span className="text-sm text-gray-300 select-none">Oui, mettre à jour le Master</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-md text-sm font-medium transition-colors" disabled={isSaving}>
                        Annuler
                    </button>
                    <button onClick={handleSave} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-md text-sm font-medium transition-colors flex items-center gap-2" disabled={isSaving}>
                        <Save className="w-4 h-4" />
                        {isSaving ? "Enregistrement..." : "Enregistrer"}
                    </button>
                </div>
            </div>
        </div>
    );
};
